import { QuoteProgressState } from './quote-progress.state';
import { Action, createReducer, on } from '@ngrx/store';
import {
    quotingCompleted, quotingCompleteMulti, quotingFailed, quotingFailedMulti,
    quotingFocus,
    quotingPendingSave,
    quotingStart,
    quotingUpdate
} from './quote-progress.actions';
import { ShipmentQuoting } from '../../models/shipment-quoting';
import { Quote } from '../../models/quote';
import { ServerMessage } from '../../models/server-message';
import { valueHash } from '../../utils/value-hash';

const initialState: QuoteProgressState = {
    shipments: [],
    focusedQuoteId: null,
    startPendingFor: null
};

function deduplicateMessages(messages: ServerMessage[]): ServerMessage[] {
    const table = {};
    return messages.filter((msg) => table[valueHash(msg.localized)] ? false : (table[valueHash(msg.localized)] = true));
}


function shipmentQuotingMerge(shipments: ShipmentQuoting[], id: string, newProgress: number, newMessages: ServerMessage[],
                              newQuotes: Quote[]): ShipmentQuoting[] {
    const existing = shipments.find(s => s.shipmentId === id);
    const newList = shipments.filter(s => s.shipmentId !== id);
    const entry = existing ? { ...existing } : { shipmentId: id, queued: false, busy: false, progress: 0, quotes: [], messages: [], completed: false };
    if (newProgress === null && newMessages === null && newQuotes === null) {
        if (entry.busy === false) {
            entry.queued = true;
        }
    } else {
        if (newProgress && !entry.completed) {
            entry.busy = newProgress < 100;
            entry.queued = false;
        }
        if (newProgress > 0 && entry.progress <= newProgress && !entry.completed) {
            entry.progress = newProgress;
            entry.queued = false;
        }
        if (newMessages) {
            entry.messages = entry.messages ? deduplicateMessages([ ...entry.messages, ...newMessages ])
                : deduplicateMessages([ ...newMessages ]);
            entry.queued = false;
        }
        if (newQuotes && newQuotes.length > 0) {
            entry.quotes = newQuotes;
            entry.queued = false;
        }
    }
    return [ ...newList, entry ];
}

const reducer = createReducer(
    initialState,
    on(quotingStart, (state, { id }) => ({
        shipments: [ ...state.shipments.filter((shipment) => shipment.shipmentId !== id).map(shipment => ({ ...shipment })), {
            shipmentId: id,
            busy: true,
            queued: true,
            progress: 0,
            messages: [],
            quotes: [],
            completed: false
        } ],
        focusedQuoteId: state.focusedQuoteId,
        startPendingFor: null
    })),
    on(quotingPendingSave, (state, { id }) => ({
        ...state,
        shipments: [ ...state.shipments ],
        startPendingFor: id
    })),
    on(quotingUpdate, (state, { id, messages, quotes, progress }) => ({
        shipments: shipmentQuotingMerge(state.shipments, id, progress, messages, quotes),
        focusedQuoteId: state.focusedQuoteId,
        startPendingFor: state.startPendingFor
    })),
    on(quotingCompleted, (state, { id, messages, quotes }) => ({
        shipments: [ ...state.shipments.filter(s => s.shipmentId !== id), {
            shipmentId: id,
            messages: deduplicateMessages([ ...state.shipments.find(s => s.shipmentId === id)?.messages, ...messages ]),
            quotes,
            busy: false,
            queued: false,
            progress: 0,
            completed: true
        } ],
        focusedQuoteId: state.focusedQuoteId,
        startPendingFor: null
    })),
    on(quotingFailed, (state, { id, messages }) => ({
        shipments: [ ...state.shipments.filter(s => s.shipmentId !== id), {
            shipmentId: id,
            messages,
            quotes: [],
            busy: false,
            queued: false,
            progress: 0,
            completed: false
        } ],
        focusedQuoteId: state.focusedQuoteId,
        startPendingFor: null
    })),
    on(quotingFocus, (state, { id }) => ({
        ...state,
        shipments: [ ...state.shipments ],
        focusedQuoteId: id
    })),
    on(quotingCompleteMulti, (state, { results }) => ({
        ...state,
        shipments: [
            /* any shipment in progress that matches the completion action should be marked complete
             * but we don't import the quotes because multi-quote won't return quotes in the completion event */
            ...state.shipments.map(s => ({
                ...s,
                busy: results.find(r => r.shipmentId === s.shipmentId) ? false : s.busy,
                progress: results.find(r => r.shipmentId === s.shipmentId) ? 100 : s.progress,
                completed: results.find(r => r.shipmentId === s.shipmentId) ? true : s.completed,
            }))
        ],
        focusedQuoteId: state.focusedQuoteId,
        startPendingFor: null
    })),
    on(quotingFailedMulti, (state, { ids, messages }) => ({
        ...state,
        shipments: [
            ...state.shipments.filter(s => ids.indexOf(s.shipmentId) === -1),
            ...ids.map(id => ({
                shipmentId: id,
                messages,
                quotes: [],
                busy: false,
                queued: false,
                progress: 0,
                completed: true
            }))
        ]
    }))
);

export function quoteProgressReducer(state: QuoteProgressState | undefined, action: Action): any {
    return reducer(state, action);
}
